import { Component, effect, inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LoadingService } from '../../../shared/services/loading.service';

import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from '../../../shared/services/alert.service';
import { AllowedDirective } from '../../../shared/services/directives/allowed.directive';
import {
  DocumentTypeEnum,
  ExportToPdfService,
} from '../../../shared/services/export-to-pdf.service';
import { ReportFilterService } from '../../../shared/services/report-filter.service';
import { FeedbackReportService } from '../../../shared/services/reporting-services/feedback-report.service';
import { ShiftReportService } from '../../../shared/services/reporting-services/shift-report.service';
import { IncentiveService } from '../../../shared/services/incentive.service';
import { IPoll } from '../../../utils/interfaces/poll';
import { IReportFilter } from '../../../utils/interfaces/report-filter';
import { IUnit } from '../../../utils/interfaces/unit';
import { ReportFiltersComponent } from '../report-filters/report-filters.component';
import { ExportDocumentTypeEnum } from '../../../shared/enums/export-document-type.enum';


export interface IReportUrl {
  title: string;
  url: DocumentTypeEnum;
  exportType: ExportDocumentTypeEnum;
  permissions: string[];
}

@Component({
  selector: 'app-report-data',
  standalone: true,
  imports: [RouterModule, AllowedDirective],
  templateUrl: './report-data.component.html',
  styleUrl: './report-data.component.scss',
})
export class ReportDataComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject();
  private readonly exportToPdfService = inject(ExportToPdfService);
  private readonly dialog = inject(MatDialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly route = inject(Router);
  private isUpdatingShiftTab = false;
  private lastShiftState: boolean | null = null;
  private lastIncentiveState = false;

  poll!: IPoll;
  units: IUnit[] = [];

  urls: IReportUrl[] = [
    {
      title: 'Resumo',
      url: DocumentTypeEnum.summary,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.consolidated'],
    },
    {
      title: 'Diário gerencial',
      url: DocumentTypeEnum.managementDiary,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.consolidated'],
    },
    {
      title: 'Engajamento',
      url: DocumentTypeEnum.engagement,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.engagement'],
    },
    {
      title: 'Feedback',
      url: DocumentTypeEnum.feedback,
      exportType: ExportDocumentTypeEnum.excel,
      permissions: ['sapore.survey.report.feedback'],
    },
    {
      title: 'Evolução',
      url: DocumentTypeEnum.evolution,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.evolution'],
    },
    {
      title: 'Analítico',
      url: DocumentTypeEnum.analytical,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.analytical'],
    },
    {
      title: 'Wordcloud',
      url: DocumentTypeEnum.wordcloud,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: [
        'sapore.survey.report.wordcloud',
        'sapore.survey.report.words',
      ],
    },
    {
      title: 'Medidor',
      url: DocumentTypeEnum.meter,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report.measurement'],
    },
    {
      title: 'Reserva de pratos',
      url: DocumentTypeEnum.reservation,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.report'],
    },
  ];

  selectedURL = this.urls[0];

  filter: IReportFilter | null = null;

  exportType = ExportDocumentTypeEnum;

  private readonly cdr = inject(ChangeDetectorRef);
  constructor(
    private readonly reportFilterService: ReportFilterService,
    private readonly alertService: AlertService,
    private readonly feedbackReportService: FeedbackReportService,
    private readonly loadingService: LoadingService,
    private readonly incentiveService: IncentiveService
  ) {
    effect(
      () => {
        const currentFilter = reportFilterService.getFilter();
        if (currentFilter && currentFilter !== this.filter) {
          this.filter = currentFilter;
          this.checkShiftsAvailability();
          this.checkIncentiveAvailability();
        }
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit(): void {
    this.poll = this.activatedRoute.snapshot.data['poll']?.data;
    this.units = this.activatedRoute.snapshot.data['unitUserPoll']?.data;

    if (this.poll.type === 1) {
      this.urls = this.urls.filter(
        (url) => url.url == DocumentTypeEnum.reservation
      );
    } else {
      this.urls = this.urls.filter(
        (url) => url.url !== DocumentTypeEnum.reservation
      );
    }

    const uniqueUrls = this.urls.filter((url, index, self) => 
      self.findIndex(u => u.url === url.url) === index
    );
    this.urls = uniqueUrls;

    const url = window.location.href;
    const lastSegment = url.split('/').pop();

    if (lastSegment) {
      this.selectedURL =
        this.urls.find((url) => url.url === lastSegment) || this.urls[0];
    }

    this.openFilterDialog();
  }

  ngOnDestroy(): void {
    this.isUpdatingShiftTab = false;
    this.lastShiftState = null;
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  openFilterDialog(): void {
    this.dialog.open(ReportFiltersComponent, {
      minWidth: 865,
      maxWidth: 865,
      data: {
        poll: this.poll,
        units: this.units,
        url: this.selectedURL.url,
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const nextRoute = this.poll.type === 1 ? 'reservation' : 'summary';
        this.route.navigate([nextRoute], { relativeTo: this.activatedRoute });
      });

  }

  exportToPdf(): void {
    if (!this.filter) {
      this.alertService.warning(
        'Não foi possível gerar o relatório, filtro não selecionado.'
      );
      return;
    }

    this.exportToPdfService.generatePDF(this.selectedURL.url);
  }

  exportToExcel(): void {
    if (!this.filter) {
      this.alertService.warning(
        'Não foi possível gerar o relatório, filtro não selecionado.'
      );
      return;
    }

    if (this.selectedURL.url !== DocumentTypeEnum.feedback) {
      this.alertService.warning(
        'Apenas o relatório de feedback pode ser exportado para Excel.'
      );
      return;
    }

    this.loadingService.showLoading();

    let questionId = '';
    this.poll.pages.forEach((p) => {
      p.questions.forEach((q) => {
        if (q.type === 3 && !questionId) {
          questionId = q.id!;
        }
      });
    });

    if (!questionId) {
      this.loadingService.closeLoading();
      this.alertService.warning(
        'Não existem perguntas do tipo texto no relatório de feedback.'
      );
      return;
    }

    this.feedbackReportService
      .getFeedbackReportExcel({ ...this.filter, questionId })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.loadingService.closeLoading());
  }

  private checkShiftsAvailability(): void {
    if (!this.filter || this.isUpdatingShiftTab) return;
    let selectedUnitWithShifts = null;
    if (
      this.filter.branchesId &&
      this.filter.branchesId.length === 1 &&
      Array.isArray(this.units) &&
      this.units.length > 0
    ) {
      selectedUnitWithShifts = this.units.find(
        (u) =>
          u &&
          u.id === this.filter!.branchesId[0] &&
          Array.isArray(u.availableShifts) &&
          u.availableShifts.length > 0
      );
    }

    const selectedShiftsCount = this.filter.avaiableShifts?.length || 0;
    let shouldShowShiftTab = false;

    if (!selectedUnitWithShifts) {
      shouldShowShiftTab = false;
    } else {
      if (selectedShiftsCount === 0) {
        shouldShowShiftTab = selectedUnitWithShifts.availableShifts.length >= 2;
      } else if (selectedShiftsCount === 1) {
        shouldShowShiftTab = false;
      } else {
        shouldShowShiftTab = true;
      }
    }

    if (this.lastShiftState !== shouldShowShiftTab) {
      this.lastShiftState = shouldShowShiftTab;
      setTimeout(() => {
        this.updateUrlsBasedOnShifts(shouldShowShiftTab);
      }, 100);
    }
  }

  private updateUrlsBasedOnShifts(hasShifts: boolean): void {
    if (this.isUpdatingShiftTab) return;
    this.isUpdatingShiftTab = true;

    try {
      const existingShiftTabs = this.urls.filter(url => url.url === DocumentTypeEnum.shift);
      const hasExistingShiftTab = existingShiftTabs.length > 0;

      if (hasShifts) {
        if (!hasExistingShiftTab) {
          const shiftTab = {
            title: 'Turno',
            url: DocumentTypeEnum.shift,
            exportType: ExportDocumentTypeEnum.pdf,
            permissions: ['sapore.survey.report.shiftcomparative'],
          };
          
          this.urls = [...this.urls, shiftTab];
        }
        else if (existingShiftTabs.length > 1) {
          this.urls = this.urls.filter(url => url.url !== DocumentTypeEnum.shift);
          
          const shiftTab = {
            title: 'Turno',
            url: DocumentTypeEnum.shift,
            exportType: ExportDocumentTypeEnum.pdf,
            permissions: ['sapore.survey.report.shiftcomparative'],
          };
          
          this.urls = [...this.urls, shiftTab];
        }
      } else {
        if (hasExistingShiftTab) {
          this.urls = this.urls.filter(url => url.url !== DocumentTypeEnum.shift);
          
          if (this.selectedURL.url === DocumentTypeEnum.shift) {
            this.selectedURL = this.urls[0];
            const nextRoute = this.poll.type === 1 ? 'reservation' : 'summary';
            this.route.navigate([nextRoute], { relativeTo: this.activatedRoute });
          }
        }
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.isUpdatingShiftTab = false;
      }, 50);
    }
  }

  private checkIncentiveAvailability(): void {
  if (!this.filter) return;

  this.incentiveService.reportByBranchId({
    startDate: this.filter.startDate,
    endDate: this.filter.finishDate,
    branchesId: this.filter.branchesId
  })
  .pipe(takeUntil(this.unsubscribe$))
  .subscribe(res => {
    const hasIncentives = !!(res.data && res.data.length > 0);
    
    if (this.lastIncentiveState !== hasIncentives) {
      this.lastIncentiveState = hasIncentives;
      this.updateIncentiveTab(hasIncentives);
    }
  });
}

private updateIncentiveTab(show: boolean): void {
  const incentiveUrl = DocumentTypeEnum.incentive;
  const exists = this.urls.some(u => u.url === incentiveUrl);

  if (show && !exists) {
    const incentiveTab = {
      title: 'Incentivo',
      url: incentiveUrl,
      exportType: ExportDocumentTypeEnum.pdf,
      permissions: ['sapore.survey.incentives.report'],
    };

    const shiftIndex = this.urls.findIndex(u => u.url === DocumentTypeEnum.shift);

    if (shiftIndex !== -1) {
      const newUrls = [...this.urls];
      newUrls.splice(shiftIndex, 0, incentiveTab);
      this.urls = newUrls;
    } else {
      this.urls = [...this.urls, incentiveTab];
    }
  } else if (!show && exists) {
    this.urls = this.urls.filter(u => u.url !== incentiveUrl);
  }  
  this.cdr.detectChanges();
}
}
